import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import { Box, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  SidebarListItemContainer,
  SidebarListItemLeftLine,
} from '@compositeComponents/sidebars/Sidebar/components/SidebarListItem/SidebarListItem.style';
import { getSidebarConfig } from '@compositeComponents/sidebars/Sidebar/sidebarConfig';
import { useAppDispatch } from '@shared/hooks/redux';
import { addProfileSidebarItem, getSidebarHubItemsForAdd } from '@store/Profile/profile.actions';

type SidebarAddItemsPopoverListProps = {
  onClose: () => void;
};

const SidebarAddItemsPopoverList: FC<SidebarAddItemsPopoverListProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [itemList, setItemList] = useState<{ id: number; name: string; tag: string }[] | null>(null);
  const skeletonArr = Array(3).fill({});

  useEffect(() => {
    dispatch(getSidebarHubItemsForAdd())
      .unwrap()
      .then(async (data) => {
        setItemList(data);
        setIsLoading(false);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  const handleAddSidebarItem = (id: number) => {
    dispatch(addProfileSidebarItem({ hubsId: id }));
    onClose();
  };

  const renderItems = itemList?.map((renderedItem, index: number) => {
    const item = getSidebarConfig(t)[renderedItem.tag];

    return (
      <SidebarListItemContainer
        onClick={() => handleAddSidebarItem(renderedItem.id)}
        key={index}
        itemColor={item.color}
        itemActiveColor={item.active?.color}
        itemActiveBackground={item.active?.background}
        itemActiveBorderColor={item.active?.borderColor}
        itemBackground={item.background}
        itemHoverColor={item?.hover?.color}
        itemHoverBackground={item?.hover?.background}
        match={false}
        sx={{
          cursor: 'pointer',
        }}
      >
        <SidebarListItemLeftLine borderColor={item.leftLineColor} match={false} />
        <item.icon />
      </SidebarListItemContainer>
    );
  });

  return isLoading ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {skeletonArr.map((_, index) => (
        <Skeleton key={index} height={36} width={'36px'} variant="rectangular" sx={{ borderRadius: '4px' }} />
      ))}
    </Box>
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>{renderItems}</Box>
  );
};

export default SidebarAddItemsPopoverList;
